import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {diff, rebase} from '@mintdata-wsp/thirdparty/jiff/jiff';
import {IJsonPatch, patch} from '@mintdata-wsp/thirdparty/jiff/lib/jsonPatch';
import {DesignEdit, SyncDesignEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {UndoRedoStack} from '@mintdata-wsp/wsp/controllers/multiplayer/UndoRedoStack';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import bind from 'bind-decorator';
import _cloneDeep from 'lodash/cloneDeep';
import _throttle from 'lodash/throttle';
import {action} from 'mobx';
import {EventBus} from '@mintdata-wsp/common/bus/EventBus';

const logger = Logger.getLogger(Category.Multiplayer);

class DesignEditWithDesign
{
    public readonly actual:DesignEdit;
    public readonly design:any;

    constructor(baseVersion:number, patches:Array<IJsonPatch>, design:any)
    {
        this.actual = {
            baseVersion,
            patches
        };
        this.design = design;
    }
}

export interface ISyncConnection
{
    sendSyncToServer(syncEvent:SyncDesignEvent, currentDesign:any):Promise<boolean>;

    setActiveDesign(newDesign:any);

    patchDesign(newDesign:any, appliedPatches?:IJsonPatch[]);
}

export class DesignBackup
{
    public version:number = 0;

    public design:any = null;

    constructor(initialVersion:number, initialDesign:any)
    {
        this.version = initialVersion;
        this.design = initialDesign;
    }
}

export class DesignShadow
{
    public version:number = 0;
    public serverVersion:number = 0;

    public sentVersion:number = 0;
    public sentServerVersion:number = 0;

    public design:any = null;

    constructor(initialVersion:number, initialDesign:any)
    {
        this.version = initialVersion;
        this.serverVersion = initialVersion;
        this.sentVersion = initialVersion;
        this.sentServerVersion = initialVersion;

        this.design = initialDesign;
    }
}

export class SyncClient
{
    private connected:boolean;

    private connection:ISyncConnection;

    private lastSyncEvent:SyncDesignEvent;
    private activeDesign:any;

    private backup:DesignBackup;

    private shadow:DesignShadow;

    private edits:Array<DesignEditWithDesign> = [];

    private undoRedoStack:UndoRedoStack;

    private syncDeltaCounter:number = 0;

    private readonly screenshotAtSyncDelta = 50;

    private readonly screenshotThrottleDelay = 120000;

    private readonly syncFromServerThreshold = 5000;

    private readonly syncToServerThreshold = 10000;

    private syncToServerTimeoutTimer = null;

    constructor(connection:ISyncConnection)
    {
        this.connected = false;
        this.connection = connection;
    }

    @bind
    public initialize(initialDesign:any, initialVersion:number):void
    {
        logger.debug('Sync client initialization...');

        // TODO[multiplayer]: this might take a lot of time,
        //  but currently needed because initialSnapshot will be changed in migration
        //  alternatively we might need to prevent the changes to the initial snapshot directly in migrations (e.g. using immer)

        logger.timer();
        initialDesign = _cloneDeep(initialDesign);
        logger.debug('Intial design cloning took: ', logger.timerEnd());

        this._resetSyncState();

        this.connected = true;

        const {multiplayer} = controllers.ui;

        EventBus.listen(multiplayer, {
            onConnectionLost: this._onConnectionLost,
        });

        this.activeDesign = initialDesign;

        this.undoRedoStack = new UndoRedoStack(initialDesign);
        this.shadow = new DesignShadow(initialVersion, initialDesign);
        this.backup = new DesignBackup(initialVersion, initialDesign);
    }

    @bind
    private _onConnectionLost()
    {
        this._resetSyncState();
    }

    @action.bound
    public onSyncFromServer(syncEvent:SyncDesignEvent):void
    {
        if( !this.connected )
            return;

        const {smartPatch} = controllers.ui;

        const {ackedVersion, edits} = syncEvent;

        const {backup, shadow} = this;

        if( edits.length === 1 && edits[0].baseVersion === shadow.serverVersion && !edits[0].patches.length )
        {
            // emptyEdit: notification that the changes from this client were applied
            this._markDesignAsSaved(true);
        }

        const activeDesign = this.activeDesign;
        const activeShadowDesign = shadow.design;

        logger.debugGroup('%cSERVER -> CLIENT :: SYNC-START', 'color: blue');

        logger.debug('SERVER -> CLIENT :: Backup version        : ', backup.version);
        logger.debug('SERVER -> CLIENT :: Shadow version        : ', shadow.version);
        logger.debug('SERVER -> CLIENT :: Shadow serverVersion  : ', shadow.serverVersion);
        logger.debug('SERVER -> CLIENT :: Acked version         : ', ackedVersion);
        logger.debug('SERVER -> CLIENT :: Active design         : ', activeDesign);
        logger.debug('SERVER -> CLIENT :: Active shadow         : ', activeShadowDesign);

        this.cleanupAckedEdits(ackedVersion);

        let relevantServerEdits = edits.filter(edit => edit.baseVersion >= shadow.serverVersion);

        if( ackedVersion < shadow.version )
        {
            logger.debug('%cSERVER -> CLIENT :: Restoring Shadow from Backup', 'color: orange');

            shadow.design = backup.design;
            shadow.version = backup.version;
            this.edits = [];

            if( ackedVersion === this.lastSyncEvent.ackedVersion )
            {
                // something went wrong on the server side - it failed to apply changes and bump version
                console.error('failed to apply patches');
                console.error(JSON.stringify([...this.edits.map(edit => edit.actual)], undefined, 4));

                this._setActiveDesign(shadow.design);

                // controllers.ui.toasts.showError(new Error("Something went wrong: the server has refused to apply latest changes. The last saved design version is restored."));

                return;
            }
        }

        // at this point backup's, shadow's and acked clienVersions should be the same
        Assert.yes(ackedVersion == shadow.version);
        Assert.yes(ackedVersion == backup.version);

        let newActiveDesign = activeDesign;
        let newShadowDesign = shadow.design;
        let newShadowServerVersion = shadow.serverVersion;

        let isSmartPatchApplied = true;

        const combinedServerEdits = smartPatch.tryToCombineEdits(relevantServerEdits);

        const allAppliedPatches:IJsonPatch[] = [];

        let thisClientHasChanges:boolean = false;

        for( const serverEdit of combinedServerEdits )
        {
            Assert.yes(serverEdit.baseVersion == newShadowServerVersion);

            logger.debug('SERVER -> CLIENT :: Applying patches from server: ', serverEdit.patches);
            if( serverEdit.patches.length > 0 )
            {
                // applying patches to shadow should always be successful
                const patchedShadowDesign = patch(serverEdit.patches, newShadowDesign);

                try
                {
                    const designToShadowPatches = diff(newShadowDesign, newActiveDesign, {invertible: true});
                    const designToShadowPatchesRebased = rebase([designToShadowPatches], serverEdit.patches);

                    thisClientHasChanges = designToShadowPatches.length > 0;

                    const isSmartPatchSuccessful = smartPatch.trySmartPatching(designToShadowPatchesRebased);

                    if( !isSmartPatchSuccessful )
                        isSmartPatchApplied = false;

                    logger.debug('SERVER -> CLIENT :: Fuzzy-patch design with server changes: ', designToShadowPatchesRebased);
                    newActiveDesign = patch(designToShadowPatchesRebased, newActiveDesign);

                    allAppliedPatches.push(...designToShadowPatchesRebased);
                }
                catch( error )
                {
                    logger.warn('%cSERVER -> CLIENT :: Can\'t apply patches to design:', 'color: red', error);
                    throw error;
                }

                newShadowDesign = patchedShadowDesign;
            }

            newShadowServerVersion++;

            logger.debug('SERVER -> CLIENT :: Server patches applied, shadow\'s serverVersion is now: ', shadow.serverVersion);
        }

        backup.design = newShadowDesign;
        shadow.design = newShadowDesign;
        shadow.serverVersion = newShadowServerVersion;

        this._setActiveDesign(newActiveDesign, allAppliedPatches, isSmartPatchApplied);

        if( newShadowDesign != activeShadowDesign )
            this.onChangedFromClient(newActiveDesign, thisClientHasChanges);

        logger.debug('%cSERVER -> CLIENT :: SYNC-END: ', 'color: blue', newActiveDesign);
        logger.debugGroupEnd();
    }

    @action.bound
    public syncGuideDesign(newDesign:any):void
    {
        if( !this.connected )
            return;

        const {activeDesign} = this;

        const diffPatches = diff(activeDesign, newDesign, {invertible: false});

        if( diffPatches.length > 0 )
        {
            const newActiveDesign = patch(diffPatches, activeDesign);

            this._setActiveDesign(newActiveDesign);

            setTimeout(commonControllers.designState.onApplicationLoad);
        }

        controllers.ui.guide.setDesignLoadingTo(false);
    }

    private cleanupAckedEdits(ackedVersion:number):void
    {
        const {backup, shadow} = this;

        Assert.yes(backup.version <= ackedVersion && ackedVersion <= shadow.version);
        if( backup.version < ackedVersion )
        {
            logger.debug('SERVER -> CLIENT :: Rewind backup forward to acknowledged version: ', ackedVersion);

            let ackedEdit = this.edits.find(edit => edit.actual.baseVersion === ackedVersion - 1);
            Assert.yes(ackedEdit, 'Edit should exist on that point');

            backup.design = ackedEdit.design;
            backup.version = ackedEdit.actual.baseVersion + 1;
        }

        Assert.yes(ackedVersion == backup.version);

        logger.debug('SERVER -> CLIENT :: Cleanup acknowledged client edits');
        this.edits = this.edits.filter(edit => edit.actual.baseVersion >= ackedVersion);
    }

    private _setActiveDesign(newDesign:any, appliedPatches?:IJsonPatch[], isSmartPatchingApplied:boolean = false):boolean
    {
        if( newDesign )
        {
            logger.debug('%cSynClient :: Set new design: ', 'color: green', newDesign);

            this.activeDesign = newDesign;

            if( !appliedPatches || appliedPatches.length && !isSmartPatchingApplied )
            {
                const {openDesign, surfaceSelection} = controllers.ui;

                openDesign.stopWatchingDesignState();

                this.connection.patchDesign(newDesign, appliedPatches);

                const selectionMode = surfaceSelection.getSelectionInteractionMode();

                if( !surfaceSelection.isSelectionMoving(selectionMode) && !surfaceSelection.isSelectionResizing(selectionMode) )
                    setTimeout(() => openDesign.startWatchingDesignState(), 100);
            }

            this.syncDeltaCounter = 0;
            return true;
        }

        return false;
    }

    public disconnect()
    {
        this.connection = null;
        this.connected = false;

        this._resetSyncState();

        logger.debug('Sync client disconnected...');
    }

    @action
    private _resetSyncState()
    {
        if( this.syncToServerTimeoutTimer )
        {
            clearTimeout(this.syncToServerTimeoutTimer);
            this.syncToServerTimeoutTimer = null;
        }

        rootState.net.lastSuccessfulSaveTimestamp = 0;
        rootState.net.isDesignSaveInProgress = false;
        rootState.net.isDesignSaveTimedOut = false;
    }

    private _markDesignAsSaved(updateTimestamp:boolean = true)
    {
        rootState.net.isDesignSaveInProgress = false;
        rootState.net.isDesignSaveTimedOut = false;

        if( updateTimestamp )
            rootState.net.lastSuccessfulSaveTimestamp = new Date().getTime();

        if( this.syncToServerTimeoutTimer !== null )
        {
            clearTimeout(this.syncToServerTimeoutTimer);
            this.syncToServerTimeoutTimer = null;
        }
    }

    @action.bound
    private _onSyncToServerTimeout()
    {
        rootState.net.isDesignSaveTimedOut = true;
        // retry
        this._onSyncToServerThrottled(this.lastSyncEvent, this.activeDesign);
    }

    @action.bound
    public onChangedFromClient(newDesign?:any, noNewDiff:boolean = false)
    {
        if( !this.connected )
            return;

        const {smartPatch, surfaceSelection} = controllers.ui;

        const {shadow} = this;

        logger.debugGroup('CLIENT -> SERVER :: SYNC-START');
        logger.debug('CLIENT -> SERVER :: Shadow version            :', shadow.version);
        logger.debug('CLIENT -> SERVER :: Shadow serverVersion      :', shadow.serverVersion);
        logger.debug('CLIENT -> SERVER :: Shadow sentVersion        :', shadow.sentVersion);
        logger.debug('CLIENT -> SERVER :: Shadow sentServerVersion  :', shadow.sentServerVersion);
        logger.debug('CLIENT -> SERVER :: Shadow design             :', shadow.design);
        logger.debug('CLIENT -> SERVER :: New design                :', newDesign);

        const diffPatches = noNewDiff ? [] : diff(shadow.design, newDesign, {invertible: false});
        logger.debug('CLIENT -> SERVER :: Diff patches: ', diffPatches);

        // console.log('%c onChangedFromClient: diffPatches =', 'background:#fcf;color:#000;', diffPatches);

        if( diffPatches.length > 0 )
        {
            // Test code for MAM-4423, safe to remove

            // const testPatch = {
            //     op: 'remove',
            //     path: '/wrongPath/test[0]'
            // }

            // diffPatches.push(testPatch as any);

            const clientEdit = new DesignEditWithDesign(shadow.version, diffPatches, newDesign);

            logger.debug('CLIENT -> SERVER :: New client edit: ', clientEdit);
            this.edits.push(clientEdit);

            shadow.design = newDesign;
            shadow.version++;

            const selectionMode = surfaceSelection.getSelectionInteractionMode();

            if( !surfaceSelection.isSelectionMoving(selectionMode) && !surfaceSelection.isSelectionResizing(selectionMode) )
                this.undoRedoStack.push(this.activeDesign, newDesign);

            this.activeDesign = newDesign;
        }

        if( shadow.sentVersion !== shadow.version || shadow.sentServerVersion !== shadow.serverVersion )
        {
            shadow.sentVersion = shadow.version;
            shadow.sentServerVersion = shadow.serverVersion;

            const actualEdits = smartPatch.tryToCombineEdits(this.edits.map(e => e.actual));

            this.lastSyncEvent = {
                ackedVersion: shadow.serverVersion,
                clientTimestamp: new Date().getTime(),
                serverTimestamp: 0,
                edits: actualEdits
            };

            if( diffPatches.length > 0 )
            {
                if( actualEdits.length )
                {
                    rootState.net.isDesignSaveInProgress = true;
                    if( this.syncToServerTimeoutTimer === null )
                        this.syncToServerTimeoutTimer = setInterval(this._onSyncToServerTimeout, this.syncToServerThreshold);
                }

                this._onSyncToServerThrottled(this.lastSyncEvent, this.activeDesign);
            }
            else
            {
                // "done applying changes" feedback: let's respond as fast as we can
                this._markDesignAsSaved(false);

                this.onSyncToServer(this.lastSyncEvent, this.activeDesign);
            }

            this._takeScreenshotBySyncDelta();
            this._takeScreenshotThrottled();
        }

        logger.debug('CLIENT -> SERVER :: SYNC-END');
        logger.debugGroupEnd();

        return true;
    }

    private readonly _onSyncToServerThrottled = _throttle(this.onSyncToServer, 500);

    private readonly _takeScreenshotThrottled = _throttle(controllers.ui.openDesign.takeDesignScreenshot, this.screenshotThrottleDelay);

    @bind
    private onSyncToServer(syncEvent:SyncDesignEvent, activeDesign:any)
    {
        if( !this.connected )
            return;

        logger.debug('%cCLIENT -> SERVER :: Send sync design event to the server: ', 'color: green', syncEvent);
        // noinspection JSIgnoredPromiseFromCall
        this.connection.sendSyncToServer(syncEvent, activeDesign);
        this.syncDeltaCounter++;
    }

    @bind
    private _takeScreenshotBySyncDelta()
    {
        if( this.syncDeltaCounter >= this.screenshotAtSyncDelta )
        {
            controllers.ui.openDesign.takeDesignScreenshot();
            this.syncDeltaCounter = 0;
        }
    }

    @bind
    public reSyncToServer():Promise<boolean>
    {
        if( !this.connected )
            return;

        logger.debug('%cCLIENT -> SERVER :: Send re-sync design event to the server: ', 'color: lime', this.lastSyncEvent);

        return this.connection.sendSyncToServer(this.lastSyncEvent, this.activeDesign);
    }

    public undo()
    {
        this._undoRedoImpl(true);
    }

    public redo()
    {
        this._undoRedoImpl(false);
    }

    private _undoRedoImpl(isUndo:boolean)
    {
        if( !this.connected )
            return;

        const {undoRedoStack, activeDesign} = this;
        let newActiveDesign;

        try
        {
            newActiveDesign = isUndo
                ? undoRedoStack.undo(activeDesign)
                : undoRedoStack.redo(activeDesign);
        }
        catch( error )
        {
            const operation = isUndo ? 'undo' : 'redo';
            // controllers.ui.toasts.showError(`Can't ${operation} due to editing conflict`);
            logger.error(`Can't ${operation} due to editing conflict`);
            return;
        }

        this._setActiveDesign(newActiveDesign);
    }

    public clearUndoStack()
    {
        if( !this.connected )
            return;

        const {undoRedoStack, activeDesign} = this;
        undoRedoStack.reset(activeDesign);
    }

    public get isUndoAllowed():boolean
    {
        return this.undoRedoStack && this.undoRedoStack.isUndoAllowed();
    }

    public get isRedoAllowed():boolean
    {
        return this.undoRedoStack && this.undoRedoStack.isRedoAllowed();
    }
}
